import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const testimonials = [
  {
    quote: "MalariaGuard transformed how we allocate resources during the rainy season. Last year we pre-positioned RDT kits in Maroua two weeks before the outbreak spike — something we never could have done before.",
    name: "Dr. Jean-Pierre Fotso",
    role: "National Epidemiologist",
    org: "Ministry of Public Health, Cameroon",
    initials: "JF",
    color: "bg-emerald-500/20 text-emerald",
  },
  {
    quote: "The confidence bands on predictions give our field teams real numbers to plan around. We went from reactive scrambling to proactive deployment across the Far North region.",
    name: "Dr. Marie-Claire Biya",
    role: "Medical Coordinator",
    org: "MSF Cameroon",
    initials: "MB",
    color: "bg-amber-500/20 text-amber",
  },
  {
    quote: "Multi-tenant access means our government partners and NGO coalition share the same data without stepping on each other. The role-based permissions are exactly what we needed.",
    name: "Lucia Abang",
    role: "Program Director",
    org: "Malaria Consortium",
    initials: "LA",
    color: "bg-teal-500/20 text-teal",
  },
];

const pricingPlans = [
  {
    name: "Field Worker",
    price: "Free",
    period: "",
    description: "For individual health workers and small community clinics",
    features: [
      "View district risk maps",
      "Receive outbreak alerts",
      "Basic weather data",
      "Mobile-friendly dashboard",
    ],
    cta: "Sign Up Free",
    popular: false,
  },
  {
    name: "Organization",
    price: "$199",
    period: "/month",
    description: "For NGOs, health ministries, and district-level teams",
    features: [
      "Everything in Field Worker",
      "Full prediction analytics",
      "Resource inventory management",
      "Export reports (CSV, PDF)",
      "Up to 25 team members",
      "Email alert notifications",
    ],
    cta: "Start Trial",
    popular: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For national health ministries and multi-country programs",
    features: [
      "Everything in Organization",
      "Custom ML model training",
      "API access & webhooks",
      "White-label deployment",
      "Unlimited users & districts",
      "Dedicated support SLA",
      "SOC-2 audit compliance",
    ],
    cta: "Contact Sales",
    popular: false,
  },
];

const techStack = [
  {
    icon: "ri-brain-line",
    title: "XGBoost Ensemble",
    desc: "Gradient-boosted decision trees trained on 2 years of weekly case data, weather, and population features. Handles non-linear relationships and feature interactions that linear models miss.",
    stat: "87%",
    statLabel: "Mean prediction confidence",
  },
  {
    icon: "ri-database-2-line",
    title: "Multi-Source Data Fusion",
    desc: "Integrates 8 distinct feature classes — historical cases, rainfall, temperature, humidity, population density, LLIN coverage, seasonality indices, and cross-district lag effects.",
    stat: "8",
    statLabel: "Feature dimensions",
  },
  {
    icon: "ri-time-line",
    title: "Temporal Cross-Validation",
    desc: "Models are retrained weekly using time-series aware validation to prevent data leakage. Walk-forward testing ensures predictions generalize to unseen future periods.",
    stat: "Weekly",
    statLabel: "Retraining cadence",
  },
  {
    icon: "ri-shield-check-line",
    title: "Probabilistic Forecasting",
    desc: "Every prediction includes 75% and 95% confidence intervals. Decision-makers see not just a point estimate but the full uncertainty landscape for resource planning.",
    stat: "95%",
    statLabel: "Confidence interval coverage",
  },
];

const partners = [
  { name: "Ministry of Public Health", initials: "MoH", color: "bg-emerald-500/15 text-emerald" },
  { name: "MSF Cameroon", initials: "MSF", color: "bg-amber-500/15 text-amber" },
  { name: "Malaria Consortium", initials: "MC", color: "bg-teal-500/15 text-teal" },
  { name: "WHO Africa", initials: "WHO", color: "bg-sky-500/15 text-sky-400" },
  { name: "CDC Foundation", initials: "CDC", color: "bg-rose-500/15 text-rose-400" },
];

const roadmap = [
  { quarter: "Q2 2025", title: "Real-Time Vector Surveillance", desc: "Integration with larval site monitoring drones and satellite imagery for breeding pool detection." },
  { quarter: "Q3 2025", title: "Genomic Resistance Tracking", desc: "Add Plasmodium falciparum drug resistance markers to prediction features for targeted ACT selection." },
  { quarter: "Q4 2025", title: "Cross-Border Early Warning", desc: "Extend model coverage to Chad, Niger, and Nigeria border regions for regional outbreak coordination." },
  { quarter: "Q1 2026", title: "Community Reporting App", desc: "SMS and WhatsApp-based case reporting from village health workers with GPS tagging and photo verification." },
];

export default function Landing() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const features = [
    {
      icon: "ri-line-chart-line",
      title: "AI-Powered Predictions",
      desc: "XGBoost models forecast malaria outbreaks 2 weeks to 3 months ahead with 87%+ confidence using weather, population, and historical data.",
    },
    {
      icon: "ri-map-2-line",
      title: "Interactive Risk Maps",
      desc: "Real-time choropleth maps show district-level risk with color-coded alerts, population overlays, and detailed district metadata.",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Intelligent Alerting",
      desc: "Smart thresholds trigger High, Medium, and Low alerts with AI-generated reasoning, contributing factors, and recommended actions.",
    },
    {
      icon: "ri-archive-line",
      title: "Resource Management",
      desc: "Track RDT kits, ACT drugs, LLIN nets, and personnel across districts. Low-stock warnings and demand gap analysis built-in.",
    },
    {
      icon: "ri-building-2-line",
      title: "Multi-Tenant Platform",
      desc: "Government ministries, NGOs, and international orgs work together with isolated data, shared predictions, and role-based access.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Secure & Compliant",
      desc: "JWT role-based authentication, encrypted API keys, activity audit logs, and SOC-2 aligned access controls for health data.",
    },
  ];

  const stats = [
    { value: "20", label: "Districts Monitored", suffix: "" },
    { value: "87", label: "Model Confidence", suffix: "%" },
    { value: "3", label: "Partner Organizations", suffix: "" },
    { value: "2yr", label: "Historical Data", suffix: "" },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200 dark:border-slate-800/60 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center">
              <img
                src="https://public.readdy.ai/ai/img_res/0a8677a0-78d8-4971-947c-fe12fe0ee52e.png"
                alt="MalariaGuard"
                className="w-9 h-9 object-contain rounded"
              />
            </div>
            <div>
              <h1 className="font-display font-bold text-sm tracking-tight text-slate-900 dark:text-white">MalariaGuard</h1>
              <p className="text-[10px] text-slate-400 font-mono tracking-wider uppercase">EpiSense AI</p>
            </div>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">Features</a>
            <a href="#technology" className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">Technology</a>
            <a href="#pricing" className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">Pricing</a>
            <a href="#testimonials" className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">Testimonials</a>
            <Link
              to="/login"
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors whitespace-nowrap"
            >
              Sign In
            </Link>
            <Link
              to="/login"
              className="px-4 py-2 rounded-lg bg-teal text-slate-950 text-sm font-semibold hover:bg-teal-hover transition-colors whitespace-nowrap"
            >
              Access Dashboard
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-8 h-8 flex items-center justify-center text-slate-500 dark:text-slate-400"
          >
            <i className={mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} />
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden px-4 py-4 border-t border-slate-200 dark:border-slate-800 space-y-3 bg-white dark:bg-slate-950">
            <a href="#features" onClick={(e) => handleNavClick(e, "features")} className="block text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white py-2">Features</a>
            <a href="#technology" onClick={(e) => handleNavClick(e, "technology")} className="block text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white py-2">Technology</a>
            <a href="#pricing" onClick={(e) => handleNavClick(e, "pricing")} className="block text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white py-2">Pricing</a>
            <a href="#testimonials" onClick={(e) => handleNavClick(e, "testimonials")} className="block text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white py-2">Testimonials</a>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium">
              Sign In
            </Link>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center px-4 py-2 rounded-lg bg-teal text-slate-950 text-sm font-semibold">
              Access Dashboard
            </Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-teal/20 rounded-full blur-[128px] animate-pulse-slow" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-amber/10 rounded-full blur-[128px] animate-pulse-slow" style={{ animationDelay: "2s" }} />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-teal/30 bg-teal/10 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" />
            <span className="text-xs font-medium text-teal">AI-Powered Outbreak Prediction</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white leading-tight mb-5">
            {t("landing_hero_title")}
            <br />
            <span className="text-teal">{t("landing_hero_subtitle")}</span>
          </h1>
          <p className="text-base md:text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            {t("landing_hero_desc")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/login"
              className="px-6 py-3 rounded-lg bg-teal text-slate-950 text-sm font-semibold hover:bg-teal-hover transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-dashboard-line text-sm" />
              </div>
              {t("landing_access_dashboard")}
            </Link>
            <a
              href="#features"
              className="px-6 py-3 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors whitespace-nowrap"
            >
              {t("landing_explore_features")}
            </a>
          </div>

          {/* Partner logos row */}
          <div className="mt-14 flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <p className="text-[10px] text-slate-400 dark:text-slate-600 uppercase tracking-widest w-full md:w-auto">{t("landing_trusted_by")}</p>
            {partners.map((p) => (
              <div key={p.name} className="flex items-center gap-2" title={p.name}>
                <div className={`w-7 h-7 flex items-center justify-center rounded-md text-[10px] font-bold ${p.color}`}>
                  {p.initials}
                </div>
                <span className="text-xs text-slate-400 dark:text-slate-500 hidden md:inline">{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 md:px-8 pb-16">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-5 text-center hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
              <p className="text-2xl md:text-3xl font-bold font-display text-slate-900 dark:text-white">
                {s.value}<span className="text-teal">{s.suffix}</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="px-4 md:px-8 py-16 border-t border-slate-200 dark:border-slate-800/60">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-slate-900 dark:text-white mb-3">
              {t("landing_features_title")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
              {t("landing_features_subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-5 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/80 transition-all group"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-muted/20 mb-4 group-hover:bg-teal-muted/30 transition-colors">
                  <i className={`${f.icon} text-teal text-lg`} />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">{f.title}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Deep Dive */}
      <section id="technology" className="px-4 md:px-8 py-16 border-t border-slate-200 dark:border-slate-800/60">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-slate-900 dark:text-white mb-3">
              {t("landing_tech_title")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
              {t("landing_tech_subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {techStack.map((t, i) => (
              <div
                key={t.title}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 hover:border-slate-300 dark:hover:border-slate-700 transition-all"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-muted/20">
                    <i className={`${t.icon} text-teal text-lg`} />
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold font-display text-slate-900 dark:text-white">{t.stat}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-500">{t.statLabel}</p>
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">{t.title}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>

          {/* Pipeline Diagram */}
          <div className="mt-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-6 text-center">Prediction Pipeline</h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-0">
              {[
                { label: "Data Ingestion", icon: "ri-database-2-line", desc: "Weather, cases, population" },
                { label: "Feature Engineering", icon: "ri-functions", desc: "Lags, rolling averages, anomalies" },
                { label: "XGBoost Model", icon: "ri-brain-line", desc: "Ensemble of 200 estimators" },
                { label: "Calibration", icon: "ri-scales-3-line", desc: "Platt scaling for confidence" },
                { label: "Alert Engine", icon: "ri-alarm-warning-line", desc: "Thresholds & reasoning" },
              ].map((step, i, arr) => (
                <div key={step.label} className="flex items-center gap-3">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                      <i className={`${step.icon} text-teal text-sm`} />
                    </div>
                    <p className="text-[10px] text-slate-800 dark:text-white mt-2 font-medium">{step.label}</p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 hidden md:block">{step.desc}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="hidden md:flex items-center px-2">
                      <i className="ri-arrow-right-line text-slate-300 dark:text-slate-600 text-xs" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Roadmap */}
          <div className="mt-10">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-6 text-center">Product Roadmap</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {roadmap.map((r, i) => (
                <div key={r.quarter} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-4 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-teal" />
                    <span className="text-[10px] font-mono uppercase tracking-wider text-teal">{r.quarter}</span>
                  </div>
                  <h4 className="text-xs font-semibold text-slate-900 dark:text-white mb-1">{r.title}</h4>
                  <p className="text-[10px] text-slate-500 dark:text-slate-500 leading-relaxed">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platform Preview */}
      <section className="px-4 md:px-8 py-16 border-t border-slate-200 dark:border-slate-800/60">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-slate-900 dark:text-white mb-3">
              The Mission Control
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
              A unified dashboard that brings together predictions, alerts, resources, and weather data in one intuitive interface.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-1 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20futuristic%20mission%20control%20dashboard%20UI%20with%20teal%20and%20amber%20accent%20charts%2C%20data%20visualization%20screens%2C%20digital%20map%20display%2C%20minimalist%20design%2C%20professional%20healthcare%20analytics%20interface%2C%20glowing%20data%20points%20on%20dark%20navy%20background%2C%20premium%20software%20interface%20mockup&width=1200&height=640&seq=malariaguard-preview-3&orientation=landscape"
              alt="MalariaGuard Dashboard Preview"
              className="w-full h-auto rounded-lg opacity-80"
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="px-4 md:px-8 py-16 border-t border-slate-200 dark:border-slate-800/60">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-slate-900 dark:text-white mb-3">
              {t("landing_testimonials_title")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
              {t("landing_testimonials_subtitle")}
            </p>
          </div>

          {/* Desktop: 3 cards side by side */}
          <div className="hidden md:grid grid-cols-3 gap-4">
            {testimonials.map((t) => (
              <div key={t.name} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 hover:border-slate-300 dark:hover:border-slate-700 transition-all flex flex-col">
                <div className="mb-4">
                  <i className="ri-double-quotes-l text-teal/30 text-2xl" />
                </div>
                <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed flex-1">{t.quote}</p>
                <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full text-xs font-bold ${t.color}`}>
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-900 dark:text-white">{t.name}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-500">{t.role}, {t.org}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: carousel */}
          <div className="md:hidden">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6">
              <div className="mb-4">
                <i className="ri-double-quotes-l text-teal/30 text-2xl" />
              </div>
              <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed mb-5">{testimonials[activeTestimonial].quote}</p>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-full text-xs font-bold ${testimonials[activeTestimonial].color}`}>
                  {testimonials[activeTestimonial].initials}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-900 dark:text-white">{testimonials[activeTestimonial].name}</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-500">{testimonials[activeTestimonial].role}, {testimonials[activeTestimonial].org}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 mt-4">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${i === activeTestimonial ? "bg-teal" : "bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-600"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-4 md:px-8 py-16 border-t border-slate-200 dark:border-slate-800/60">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-slate-900 dark:text-white mb-3">
              {t("landing_pricing_title")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
              {t("landing_pricing_subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-xl border p-6 transition-all ${
                  plan.popular
                    ? "border-teal/40 bg-white dark:bg-slate-900/80 relative"
                    : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 hover:border-slate-300 dark:hover:border-slate-700"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-teal text-slate-950 text-[10px] font-semibold">
                    Most Popular
                  </div>
                )}
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">{plan.name}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-500 mb-4">{plan.description}</p>
                <div className="mb-5">
                  <span className="text-2xl font-bold font-display text-slate-900 dark:text-white">{plan.price}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-500">{plan.period}</span>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-teal text-sm" />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.name === "Enterprise" ? "/signup" : "/login"}
                  className={`block w-full text-center py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                    plan.popular
                      ? "bg-teal text-slate-950 hover:bg-teal-hover"
                      : "border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 md:px-8 py-16 border-t border-slate-200 dark:border-slate-800/60">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 md:p-10 text-center">
            <h2 className="text-xl md:text-2xl font-display font-bold text-slate-900 dark:text-white mb-3">
              {t("landing_cta_title")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-lg mx-auto mb-6">
              {t("landing_cta_desc")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/login"
                className="px-6 py-3 rounded-lg bg-teal text-slate-950 text-sm font-semibold hover:bg-teal-hover transition-colors whitespace-nowrap"
              >
                {t("landing_get_started")}
              </Link>
              <Link
                to="/signup"
                className="px-6 py-3 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors whitespace-nowrap"
              >
                {t("landing_request_access")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 md:px-8 py-8 border-t border-slate-200 dark:border-slate-800/60">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 flex items-center justify-center">
              <img
                src="https://public.readdy.ai/ai/img_res/0a8677a0-78d8-4971-947c-fe12fe0ee52e.png"
                alt="MalariaGuard"
                className="w-7 h-7 object-contain rounded opacity-60"
              />
            </div>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              &copy; 2025 MalariaGuard. EpiSense AI Platform.
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400 dark:text-slate-500">
            <span>{t("landing_footer_moh")}</span>
            <span className="hidden sm:inline">|</span>
            <span>{t("landing_footer_built")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}